/**
 * Password Validation Utility
 *
 * Enforces strong password requirements:
 * - Minimum 12 characters
 * - At least 1 lowercase letter (a-z)
 * - At least 1 uppercase letter (A-Z)
 * - At least 1 special character: ! @ # $ % ^ & * ( ) _ + - = [ ] { } ; ' : " \ | , . < > / ?
 * - ASCII characters only (no unicode, emojis, accented characters)
 *
 * Shared across client-saas and admin applications.
 *
 * @param {string} password - The password to validate
 * @returns {string[]} - Array of error messages (empty if valid)
 *
 * @example
 * const errors = validatePassword('MyPassword123!');
 * if (errors.length === 0) {
 *   console.log('Valid password');
 * } else {
 *   console.log('Password must have:', errors.join(', '));
 * }
 */
export function validatePassword(password) {
  const errors = [];

  // Check length. MUST NOT be lower than the server's own minimum in
  // apps/service-api/src/services/credential.js (assertUsablePassword), which
  // throws below 12 characters. When this was 10, an 11-character password
  // passed client validation and then threw server-side, surfacing as a generic
  // 500 "Signup failed" with no indication of the real cause.
  if (password.length < 12) {
    errors.push('at least 12 characters');
  }

  // Upper bound mirrors the server's 1024-byte cap on hashing input.
  if (password.length > 1024) {
    errors.push('at most 1024 characters');
  }

  // Check for lowercase
  if (!/[a-z]/.test(password)) {
    errors.push('1 lowercase');
  }

  // Check for uppercase
  if (!/[A-Z]/.test(password)) {
    errors.push('1 uppercase');
  }

  // Check for special character
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('1 special character');
  }

  // Check for ASCII only (0-127)
  if (/[^\x00-\x7F]/.test(password)) {
    errors.push('ASCII characters only');
  }

  return errors;
}

/**
 * Check if password is valid
 * @param {string} password - The password to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isPasswordValid(password) {
  return validatePassword(password).length === 0;
}

/**
 * Format validation errors into user-friendly message
 * Phone-friendly: Max 2 lines (pipe-delimited for toast)
 * @param {string[]} errors - Array of error messages from validatePassword()
 * @returns {string} - Formatted error message
 */
export function formatPasswordErrors(errors) {
  if (errors.length === 0) {
    return '';
  }

  // Special case: Non-ASCII characters (emoji, unicode, etc.)
  if (errors.includes('ASCII characters only')) {
    return 'Password cannot contain emoji|or special unicode characters';
  }

  // Special case: length issue. Derived from the validator's ACTUAL error
  // strings ('at least 12 characters' / 'at most 1024 characters') instead of
  // hardcoding a copy — a hardcoded 'at least 10 characters' went stale when
  // the minimum moved to 12, and length failures silently fell through to the
  // character-type message below.
  const lengthError = errors.find((e) => /^at (?:least|most) \d+ characters$/.test(e));
  if (lengthError) {
    return `Password must have ${lengthError.replace(/ (\d+ characters)$/, '|$1')}`;
  }

  // General case: Missing character types
  return 'Password must have: Uppercase,|Lowercase, and Special Character';
}
